import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Calendar as CalendarIcon, CheckCircle, Clock, Video, Download, Sparkles, ArrowLeft, ArrowRight, Loader2, AlertCircle } from 'lucide-react';
import { api } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { trackBooking } from '../lib/analytics';
import { downloadClientIcsFile } from '../lib/ical';

export default function Booking() {
  const [step, setStep] = useState(1); // 1: Consultation Type, 2: Date & Time, 3: Contact Info & Confirm
  const [selectedType, setSelectedType] = useState('Initial Consultation');
  const [selectedDate, setSelectedDate] = useState(() => {
    // Default to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  });
  const [selectedSlot, setSelectedSlot] = useState(null);

  // Available slots state
  const [slotsData, setSlotsData] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotError, setSlotError] = useState(null);

  // Form inputs
  const [form, setForm] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    message: '',
    marketingConsent: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(null);

  const { toast } = useToast();

  const consultationTypes = [
    {
      id: 'Initial Consultation',
      title: 'Initial Consultation',
      duration: '30 minutes',
      price: 'Free',
      badge: 'Most Popular',
      description: 'Discuss your primary financial goals and outline initial tax-free wealth strategies.',
      features: ['Goal & portfolio assessment', 'Tax-free wealth strategy overview', 'Next steps roadmap']
    },
    {
      id: 'Comprehensive Planning',
      title: 'Comprehensive Planning',
      duration: '60 minutes',
      price: 'Free',
      badge: 'In-Depth Review',
      description: 'Detailed financial needs analysis, asset protection review, and customized roadmap.',
      features: ['Complete Financial Needs Analysis (FNA)', 'Asset protection & 0% floor review', 'Tailored execution roadmap']
    },
    {
      id: 'Follow-up Session',
      title: 'Follow-up Session',
      duration: '30 minutes',
      price: 'Free',
      badge: 'Existing Clients',
      description: 'Review ongoing progress, adjust asset allocations, and optimize wealth strategies.',
      features: ['Progress & performance review', 'Strategy adjustments', 'Ongoing advisory support']
    }
  ];

  // Fetch real-time available time slots when date or consultation type changes
  const fetchSlots = useCallback(async (dateStr, typeStr) => {
    setLoadingSlots(true);
    setSlotError(null);
    try {
      const res = await api.getAvailableSlots(dateStr, typeStr);
      if (res?.data) {
        setSlotsData(res.data);
      } else {
        setSlotsData(null);
      }
    } catch (err) {
      console.error('Error fetching available slots:', err);
      setSlotError('Could not load slots for selected date. Please try another day.');
    } finally {
      setLoadingSlots(false);
    }
  }, []);

  useEffect(() => {
    if (step === 2 && selectedDate) {
      fetchSlots(selectedDate, selectedType);
    }
  }, [step, selectedDate, selectedType, fetchSlots]);

  function update(field) {
    return (e) => setForm({ ...form, [field]: e.target.value });
  }

  function updateCheckbox(field) {
    return (e) => setForm({ ...form, [field]: e.target.checked });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!selectedSlot) {
      toast({
        title: 'Select a Time Slot',
        description: 'Please select an available time slot for your consultation.',
        variant: 'destructive'
      });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        firstname: form.firstname,
        lastname: form.lastname,
        email: form.email,
        phone: form.phone,
        consultation_type: selectedType,
        meeting_preferred_date: selectedDate,
        preferred_time: selectedSlot.label,
        message: form.message,
        marketing_consent: String(form.marketingConsent),
        consentTimestamp: new Date().toISOString()
      };

      const res = await api.booking(payload);
      trackBooking();

      const meetLink = res?.data?.meetLink || 'https://meet.google.com/piw-qsyu-fqg';
      setBookingSuccess({
        ...payload,
        meetLink
      });

      toast({
        title: 'Consultation Confirmed! 🎉',
        description: 'Your strategy session is scheduled. Check your email for details & calendar invite.',
        variant: 'success'
      });
    } catch (err) {
      toast({
        title: 'Booking Error',
        description: err.message || 'Submission failed. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  }

  // Calculate min date (tomorrow)
  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);
  const minDateStr = minDate.toISOString().split('T')[0];

  return (
    <section id="booking" className="relative py-24 bg-[#0D1B2A] text-white overflow-hidden">
      {/* Background Glows */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute top-1/4 -right-32 w-[500px] h-[500px] bg-[#C9A84C]/10 rounded-full blur-[150px] animate-pulse-glow" />
        <div className="absolute bottom-10 left-10 w-[500px] h-[500px] bg-[#3D6ACC]/15 rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#162236] border border-[#C9A84C]/30 text-xs sm:text-sm font-semibold text-[#E8D4A0] uppercase tracking-wider">
            <Sparkles className="w-4 h-4 text-[#C9A84C]" />
            Direct 1-on-1 Consultation • Live Calendar Sync
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-playfair tracking-tight">
            Schedule Your <span className="text-gradient-gold">1-on-1 Strategy Session</span>
          </h2>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto font-opensans">
            Select your preferred consultation type and pick a time slot directly on our live calendar.
          </p>
        </div>

        {/* Step Progress Bar (If not confirmed) */}
        {!bookingSuccess && (
          <div className="max-w-3xl mx-auto mb-10">
            <div className="flex items-center justify-between text-xs sm:text-sm font-semibold text-slate-300">
              <span className={step >= 1 ? 'text-[#E8D4A0]' : ''}>1. Select Service</span>
              <span className={step >= 2 ? 'text-[#E8D4A0]' : ''}>2. Pick Date & Time</span>
              <span className={step >= 3 ? 'text-[#E8D4A0]' : ''}>3. Confirm Details</span>
            </div>
            <div className="w-full bg-[#162236] h-2 rounded-full mt-3 overflow-hidden border border-[#C9A84C]/20">
              <div
                className="bg-gradient-to-r from-[#C9A84C] to-[#E8D4A0] h-full transition-all duration-500 ease-out"
                style={{ width: `${(step / 3) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Success Confirmation View */}
        {bookingSuccess ? (
          <div className="max-w-2xl mx-auto bg-[#162236]/90 border border-[#C9A84C]/40 rounded-3xl p-8 sm:p-10 text-center space-y-6 shadow-2xl backdrop-blur-xl">
            <div className="w-16 h-16 bg-[#C9A84C]/20 border border-[#C9A84C] rounded-full flex items-center justify-center mx-auto text-[#C9A84C]">
              <CheckCircle className="w-10 h-10" />
            </div>

            <h3 className="text-2xl sm:text-3xl font-bold font-playfair text-white">
              Consultation Successfully Booked!
            </h3>

            <p className="text-slate-300 text-base leading-relaxed">
              Thank you, <strong className="text-white">{bookingSuccess.firstname}</strong>! Your strategy session is confirmed. A calendar invite & confirmation email have been sent to <strong className="text-[#E8D4A0]">{bookingSuccess.email}</strong>.
            </p>

            {/* Booking Details Card */}
            <div className="bg-[#0D1B2A] border border-[#C9A84C]/30 rounded-2xl p-6 text-left space-y-3">
              <div className="flex items-center gap-3 text-slate-200">
                <CalendarIcon className="w-5 h-5 text-[#C9A84C]" />
                <span><strong>Date:</strong> {bookingSuccess.meeting_preferred_date}</span>
              </div>
              <div className="flex items-center gap-3 text-slate-200">
                <Clock className="w-5 h-5 text-[#C9A84C]" />
                <span><strong>Time Slot:</strong> {bookingSuccess.preferred_time} (Central Time - CT)</span>
              </div>
              <div className="flex items-center gap-3 text-slate-200">
                <Video className="w-5 h-5 text-[#C9A84C]" />
                <span><strong>Format:</strong> Google Meet Video Call</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-2">
              {bookingSuccess.meetLink && (
                <a
                  href={bookingSuccess.meetLink}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r from-[#C9A84C] to-[#8C6D23] text-[#070E1B] font-bold shadow-lg hover:brightness-110 transition-all"
                >
                  <Video className="w-5 h-5" />
                  Join Google Meet Video Call
                </a>
              )}
              <Button
                type="button"
                onClick={() => downloadClientIcsFile(bookingSuccess, bookingSuccess.meetLink)}
                className="bg-[#101F33] hover:bg-[#1A2E4B] text-white border border-[#C9A84C]/40 px-6 py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2"
              >
                <Download className="w-5 h-5 text-[#C9A84C]" />
                Download .ics Calendar File
              </Button>
            </div>

            <button
              onClick={() => {
                setBookingSuccess(null);
                setStep(1);
                setSelectedSlot(null);
              }}
              className="text-xs text-slate-400 hover:text-slate-200 underline pt-4 block mx-auto"
            >
              Book Another Session
            </button>
          </div>
        ) : (
          <div className="max-w-5xl mx-auto">
            {/* STEP 1: Select Consultation Type */}
            {step === 1 && (
              <div className="space-y-8">
                <div className="grid md:grid-cols-3 gap-6">
                  {consultationTypes.map((type) => {
                    const isSelected = selectedType === type.id;
                    return (
                      <div
                        key={type.id}
                        onClick={() => setSelectedType(type.id)}
                        className={`cursor-pointer rounded-2xl p-6 transition-all duration-300 flex flex-col justify-between relative border ${
                          isSelected
                            ? 'bg-[#162236] border-[#C9A84C] shadow-2xl ring-2 ring-[#C9A84C]/50'
                            : 'bg-[#101F33]/80 border-[#C9A84C]/20 hover:border-[#C9A84C]/50 hover:bg-[#162236]/60'
                        }`}
                      >
                        <div className="space-y-4">
                          <div className="flex justify-between items-start">
                            <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-[#0D1B2A] text-[#E8D4A0] border border-[#C9A84C]/30">
                              {type.badge}
                            </span>
                            <span className="text-sm font-semibold text-[#C9A84C]">{type.duration}</span>
                          </div>

                          <div>
                            <h3 className="text-xl font-bold font-playfair text-white">{type.title}</h3>
                            <p className="text-sm text-slate-300 mt-2 leading-relaxed">{type.description}</p>
                          </div>

                          <ul className="space-y-2 pt-2">
                            {type.features.map((feat, i) => (
                              <li key={i} className="text-xs text-slate-300 flex items-center gap-2">
                                <CheckCircle className="w-3.5 h-3.5 text-[#C9A84C] shrink-0" />
                                {feat}
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div className="pt-6">
                          <Button
                            type="button"
                            className={`w-full rounded-xl font-bold ${
                              isSelected
                                ? 'bg-[#C9A84C] hover:bg-[#B3933B] text-[#070E1B]'
                                : 'bg-[#0D1B2A] text-slate-300 hover:text-white border border-[#C9A84C]/30'
                            }`}
                          >
                            {isSelected ? 'Selected' : 'Select Option'}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="text-center pt-4">
                  <Button
                    type="button"
                    onClick={() => setStep(2)}
                    className="bg-gradient-to-r from-[#C9A84C] to-[#8C6D23] hover:brightness-110 text-[#070E1B] font-bold text-base px-10 py-6 rounded-xl shadow-xl inline-flex items-center gap-2"
                  >
                    Continue to Date & Time Selection
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 2: Pick Date & Time Slot */}
            {step === 2 && (
              <div className="bg-[#162236]/90 border border-[#C9A84C]/30 rounded-3xl p-6 sm:p-10 shadow-2xl backdrop-blur-xl space-y-8">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-slate-800 pb-6">
                  <div>
                    <span className="text-xs text-[#C9A84C] font-semibold tracking-wider uppercase">Selected Service</span>
                    <h3 className="text-xl font-bold font-playfair text-white">{selectedType}</h3>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setStep(1)}
                    className="text-xs text-slate-400 hover:text-white flex items-center gap-1.5"
                  >
                    <ArrowLeft className="w-4 h-4" /> Change Service
                  </Button>
                </div>

                <div className="grid lg:grid-cols-12 gap-8 items-start">
                  {/* Left: Date Selection */}
                  <div className="lg:col-span-5 space-y-4">
                    <label className="block text-sm font-bold uppercase text-slate-300 tracking-wider">
                      1. Select Consultation Date
                    </label>
                    <Input
                      type="date"
                      min={minDateStr}
                      value={selectedDate}
                      onChange={(e) => {
                        setSelectedDate(e.target.value);
                        setSelectedSlot(null);
                      }}
                      className="h-13 bg-[#0D1B2A] border border-[#C9A84C]/40 text-white focus:border-[#C9A84C] rounded-xl text-base px-4 font-semibold"
                    />

                    {slotsData && (
                      <div className="bg-[#0D1B2A]/80 border border-[#C9A84C]/20 p-4 rounded-xl space-y-2 text-xs text-slate-300">
                        <div className="flex justify-between">
                          <span>Day of Week:</span>
                          <strong className="text-white">{slotsData.dayOfWeek}</strong>
                        </div>
                        <div className="flex justify-between">
                          <span>Available Slots:</span>
                          <strong className="text-[#C9A84C]">{slotsData.totalAvailable} slots open</strong>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right: Real-time Slot Selection */}
                  <div className="lg:col-span-7 space-y-4">
                    <label className="block text-sm font-bold uppercase text-slate-300 tracking-wider">
                      2. Choose Available Time Slot <span className="text-xs text-[#E8D4A0] font-semibold tracking-normal lowercase ml-1">(All times in Central Time - CT)</span>
                    </label>

                    {loadingSlots ? (
                      <div className="py-12 text-center text-slate-400 space-y-3">
                        <Loader2 className="w-8 h-8 text-[#C9A84C] animate-spin mx-auto" />
                        <p className="text-sm">Checking live Google Calendar & slot availability...</p>
                      </div>
                    ) : slotError ? (
                      <div className="py-8 text-center text-red-400 space-y-2 bg-[#0D1B2A] border border-red-500/30 rounded-2xl p-4">
                        <AlertCircle className="w-6 h-6 mx-auto" />
                        <p className="text-sm">{slotError}</p>
                      </div>
                    ) : slotsData?.availableSlots ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          {slotsData.availableSlots.map((slot, index) => {
                            const isSelected = selectedSlot?.time === slot.time;
                            const isAvailable = slot.available;

                            return (
                              <button
                                key={index}
                                type="button"
                                disabled={!isAvailable}
                                onClick={() => setSelectedSlot(slot)}
                                className={`py-3 px-4 rounded-xl text-sm font-bold transition-all duration-200 flex items-center justify-between border ${
                                  isSelected
                                    ? 'bg-[#C9A84C] text-[#070E1B] border-[#C9A84C] shadow-lg scale-[1.02]'
                                    : isAvailable
                                    ? 'bg-[#0D1B2A] text-slate-200 border-[#C9A84C]/30 hover:border-[#C9A84C] hover:text-white'
                                    : 'bg-[#0D1B2A]/40 text-slate-600 border-slate-800 cursor-not-allowed opacity-50'
                                }`}
                              >
                                <span>{slot.label}</span>
                                {isAvailable ? (
                                  <span className={`w-2 h-2 rounded-full ${isSelected ? 'bg-[#070E1B]' : 'bg-emerald-400'}`} />
                                ) : (
                                  <span className="text-[10px] uppercase text-slate-500">Booked</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>

                {/* Bottom Navigation */}
                <div className="flex justify-between items-center pt-6 border-t border-slate-800">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setStep(1)}
                    className="text-slate-400 hover:text-white"
                  >
                    Back
                  </Button>
                  <Button
                    type="button"
                    disabled={!selectedSlot}
                    onClick={() => setStep(3)}
                    className="bg-gradient-to-r from-[#C9A84C] to-[#8C6D23] hover:brightness-110 text-[#070E1B] font-bold px-8 py-3.5 rounded-xl shadow-lg flex items-center gap-2"
                  >
                    Continue to Details
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 3: Client Info & Final Submission */}
            {step === 3 && (
              <div className="bg-[#162236]/90 border border-[#C9A84C]/30 rounded-3xl p-6 sm:p-10 shadow-2xl backdrop-blur-xl space-y-8">
                {/* Summary Header */}
                <div className="bg-[#0D1B2A] border border-[#C9A84C]/30 p-6 rounded-2xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div className="space-y-1">
                    <span className="text-xs text-[#C9A84C] font-semibold uppercase tracking-wider">Booking Summary</span>
                    <h4 className="text-lg font-bold text-white">{selectedType}</h4>
                    <p className="text-xs text-slate-300">
                      📅 {selectedDate} &bull; ⏰ {selectedSlot?.label} (Central Time - CT) &bull; 🎥 Google Meet Call
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setStep(2)}
                    className="text-xs text-slate-400 hover:text-white flex items-center gap-1"
                  >
                    <ArrowLeft className="w-4 h-4" /> Change Time Slot
                  </Button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs font-semibold uppercase text-slate-300 tracking-wider mb-2">
                        First Name *
                      </label>
                      <Input
                        type="text"
                        required
                        placeholder="John"
                        value={form.firstname}
                        onChange={update('firstname')}
                        className="h-12 bg-[#0D1B2A] border border-[#C9A84C]/30 text-white placeholder-slate-500 rounded-xl px-4"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold uppercase text-slate-300 tracking-wider mb-2">
                        Last Name *
                      </label>
                      <Input
                        type="text"
                        required
                        placeholder="Doe"
                        value={form.lastname}
                        onChange={update('lastname')}
                        className="h-12 bg-[#0D1B2A] border border-[#C9A84C]/30 text-white placeholder-slate-500 rounded-xl px-4"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs font-semibold uppercase text-slate-300 tracking-wider mb-2">
                        Email Address *
                      </label>
                      <Input
                        type="email"
                        required
                        placeholder="john@example.com"
                        value={form.email}
                        onChange={update('email')}
                        className="h-12 bg-[#0D1B2A] border border-[#C9A84C]/30 text-white placeholder-slate-500 rounded-xl px-4"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold uppercase text-slate-300 tracking-wider mb-2">
                        Phone Number (Optional)
                      </label>
                      <Input
                        type="tel"
                        placeholder="(555) 000-0000"
                        value={form.phone}
                        onChange={update('phone')}
                        className="h-12 bg-[#0D1B2A] border border-[#C9A84C]/30 text-white placeholder-slate-500 rounded-xl px-4"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold uppercase text-slate-300 tracking-wider mb-2">
                      Specific Notes / Financial Goals (Optional)
                    </label>
                    <textarea
                      rows={3}
                      placeholder="Share any specific topics or goals you would like to cover..."
                      value={form.message}
                      onChange={update('message')}
                      className="w-full bg-[#0D1B2A] border border-[#C9A84C]/30 text-white placeholder-slate-500 rounded-xl p-4 text-sm focus:border-[#C9A84C] focus:ring-1 focus:ring-[#C9A84C]"
                    />
                  </div>

                  {/* Consents */}
                  <div className="space-y-3 pt-2">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="marketingConsentBooking"
                        checked={form.marketingConsent}
                        onChange={updateCheckbox('marketingConsent')}
                        className="mt-1 h-4 w-4 rounded accent-[#C9A84C] bg-[#0D1B2A] border-[#C9A84C]/40"
                      />
                      <label htmlFor="marketingConsentBooking" className="text-xs text-slate-300 leading-relaxed">
                        I agree to receive booking updates & financial strategy information from Grow & Stay Rich.
                      </label>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-between items-center pt-4">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setStep(2)}
                      className="text-slate-400 hover:text-white"
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="bg-gradient-to-r from-[#C9A84C] to-[#8C6D23] hover:brightness-110 text-[#070E1B] font-bold text-base px-10 py-6 rounded-xl shadow-xl flex items-center gap-2"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Confirming Booking...
                        </>
                      ) : (
                        <>
                          Confirm & Book Consultation
                          <CheckCircle className="w-5 h-5" />
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}